/**
 * lib/theme.ts — the viewer's theme choice, and the one state that was previously unreachable.
 *
 * **There are three choices, not two.** The stylesheet follows `prefers-color-scheme` while
 * `data-theme` is unset, and beats it in both directions when it is set — so the *absence* of the
 * attribute IS the "follow the system" state. Until this module existed the client wrote the attribute
 * on the first toggle and never removed it, which made that state unreachable: a viewer whose operating
 * system is dark, who pressed the toggle once, was pinned to the light palette for ever. That is
 * precisely the outcome `main.tsx`'s own docblock warned about, closed one layer up instead of one
 * layer down.
 *
 * **`THEME_KEY` lives here and only here.** It was declared twice as unlinked string literals — once in
 * `App.tsx` for the toggle and once in `main.tsx` for the pre-paint seed — so changing one silently
 * broke the other: the seed would read a key nothing writes, and every cold load would flash the wrong
 * palette. One home, two importers.
 *
 * Storage is wrapped in `try`/`catch` throughout. A browser with storage disabled still gets the theme
 * for this session; it simply forgets it.
 */

export type ThemeChoice = "light" | "dark" | "system";

export const THEME_KEY = "ticketless.theme";

/** The three, in the order the control offers them. Exported so the control cannot drift from the type. */
export const THEME_CHOICES: readonly ThemeChoice[] = ["light", "dark", "system"];

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === "light" || value === "dark" || value === "system";
}

/**
 * What the viewer last chose. Absent, unreadable or corrupt all read as `"system"`.
 *
 * A stored value this module does not recognise is deliberately NOT an error state: it is a value from
 * an older build, or somebody's console. Following the operating system is the right answer to every
 * one of those, and it is the answer that pins nobody.
 */
export function readThemeChoice(): ThemeChoice {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(THEME_KEY);
  } catch {
    stored = null;
  }
  return isThemeChoice(stored) ? stored : "system";
}

/**
 * Apply a choice to the document, and remember it.
 *
 * `"system"` REMOVES both the attribute and the stored value rather than writing a third value into
 * either. The stylesheet has no `:root[data-theme="system"]` rule and must not grow one — the whole
 * mechanism is that an unset attribute lets `@media (prefers-color-scheme: dark)` decide.
 */
export function applyThemeChoice(choice: ThemeChoice): void {
  if (choice === "system") {
    delete document.documentElement.dataset["theme"];
    try {
      window.localStorage.removeItem(THEME_KEY);
    } catch {
      // Nothing to forget; the attribute is already gone, which is what the viewer asked for.
    }
    return;
  }

  document.documentElement.dataset["theme"] = choice;
  try {
    window.localStorage.setItem(THEME_KEY, choice);
  } catch {
    // The choice holds for this session and is forgotten on the next load.
  }
}

/**
 * What `main.tsx` runs at module scope, before the first paint.
 *
 * Deliberately not `applyThemeChoice(readThemeChoice())`: that would *write* on every cold load, and a
 * `removeItem` on a key that is already absent is a storage access for nothing. This only ever sets.
 */
export function seedTheme(): void {
  const choice = readThemeChoice();
  if (choice === "system") return;
  document.documentElement.dataset["theme"] = choice;
}
